import { useEffect, useState } from "react";
import {
  getStorage, ref, getDownloadURL, getMetadata, listAll
} from "firebase/storage";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { detectLang, t } from "../i18n";
import LoadingSpinner from "./LoadingSpinner";
import "./Curso.css";

const estructuraCurso = {
  es: [
    {
      id: "modulo1",
      titulo: "¿Cómo me registro?",
      lecciones: [
        { id: "leccion1", titulo: "1. Primer paso" },
      ],
    },
    {
      id: "modulo2",
      titulo: "Panel de trabajo",
      lecciones: [
        { id: "leccion1", titulo: "1. Introducción general" },
        { id: "leccion2", titulo: "2. Cómo crear link de referido" },
        { id: "leccion3", titulo: "3. Cómo usar Media Report" },
        { id: "leccion4", titulo: "4. Configuraciones de billetera" },
        { id: "leccion5", titulo: "5. Sub-afiliados y comisiones" },
      ],
    },
    {
      id: "modulo3",
      titulo: "Crear mi comunidad desde 0",
      lecciones: [
        { id: "leccion1", titulo: "1. Crear mi marca" },
        { id: "leccion2", titulo: "2. Redes sociales" },
        { id: "leccion3", titulo: "3. ¿Cómo trabajo?" },
        { id: "leccion4", titulo: "4. ¿Cómo crear mi comunidad?" },
      ],
    },
    {
      id: "modulo4",
      titulo: "¿Cómo creo contenido?",
      lecciones: [
        { id: "leccion1", titulo: "1. ¿Qué herramientas necesito?" },
        { id: "leccion2", titulo: "2. ¿Qué apps utilizar?" },
        { id: "leccion3", titulo: "3. ¿Cómo viralizar?" },
      ],
    },
    {
      id: "modulo5",
      titulo: "Bonus",
      lecciones: [
        { id: "leccion1", titulo: "1. Preguntas frecuentes" },
        { id: "leccion2", titulo: "2. Drive informativo" },
      ],
    },
  ],
  en: [
    {
      id: "modulo1",
      titulo: "How do I register?",
      lecciones: [
        { id: "leccion1", titulo: "1. First step" },
      ],
    },
    {
      id: "modulo2",
      titulo: "Work panel",
      lecciones: [
        { id: "leccion1", titulo: "1. General introduction" },
        { id: "leccion2", titulo: "2. How to create referral link" },
        { id: "leccion3", titulo: "3. How to use Media Report" },
        { id: "leccion4", titulo: "4. Wallet settings" },
        { id: "leccion5", titulo: "5. Sub-affiliates and commissions" },
      ],
    },
    {
      id: "modulo3",
      titulo: "Build my community from scratch",
      lecciones: [
        { id: "leccion1", titulo: "1. Build my brand" },
        { id: "leccion2", titulo: "2. Social media" },
        { id: "leccion3", titulo: "3. How do I work?" },
        { id: "leccion4", titulo: "4. How to build my community?" },
      ],
    },
    {
      id: "modulo4",
      titulo: "How do I create content?",
      lecciones: [
        { id: "leccion1", titulo: "1. What tools do I need?" },
        { id: "leccion2", titulo: "2. What apps to use?" },
        { id: "leccion3", titulo: "3. How to go viral?" },
      ],
    },
    {
      id: "modulo5",
      titulo: "Bonus",
      lecciones: [
        { id: "leccion1", titulo: "1. Frequently asked questions" },
        { id: "leccion2", titulo: "2. Info drive" },
      ],
    },
  ],
};

const Curso = () => {
  const { cursoId, moduloId } = useParams();
  const navigate = useNavigate();

  const [userName, setUserName] = useState("");
  const [lecciones, setLecciones] = useState([]);
  const [leccionSeleccionada, setLeccionSeleccionada] = useState(null);
  const [progreso, setProgreso] = useState(0);
  const [completadas, setCompletadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLangState] = useState(detectLang());

  const auth = getAuth();
  const db = getFirestore();

  const handleLang = (l) => {
    setLangState(l);
    localStorage.setItem("lang", l);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDoc = await getDoc(doc(db, "usuarios", user.uid));
          if (userDoc.exists()) {
            setUserName(userDoc.data().nombre || "Usuario");
          }
          await cargarModulo(lang);
        } else {
          navigate("/");
        }
      });
      return () => unsubscribe();
    };
    init();
  }, [cursoId, moduloId, lang]);

  const cargarModulo = async (currentLang) => {
  const storage = getStorage();
  const estructura = estructuraCurso[currentLang] || estructuraCurso["es"];
  const modulo = estructura.find(m => m.id === moduloId) || estructura[0];

  const cacheKey = `cache_${cursoId}_${currentLang}_${modulo.id}`;
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    const leccionesCached = JSON.parse(cached);
    setLecciones(leccionesCached);
    const primera = leccionesCached.find(l => l.disponible) || leccionesCached[0];
    setLeccionSeleccionada(primera);
    setLoading(false);
    return;
  }

  const leccionesConVideo = await Promise.all(
    modulo.lecciones.map(async (leccion) => {
      try {
        const carpetaRef = ref(storage, `cursos/${cursoId}/${currentLang}/${modulo.id}/${leccion.id}`);
        const lista = await listAll(carpetaRef);
        if (lista.items.length > 0) {
          const url = await getDownloadURL(lista.items[0]);
          const meta = await getMetadata(lista.items[0]);
          const tipo = meta.contentType.startsWith("video/") ? "video" : "imagen";
          return { ...leccion, url, tipo, disponible: true };
        }
        return { ...leccion, url: null, tipo: null, disponible: false };
      } catch {
        return { ...leccion, url: null, tipo: null, disponible: false };
      }
    })
  );

  localStorage.setItem(cacheKey, JSON.stringify(leccionesConVideo));
  setLecciones(leccionesConVideo);
  const primera = leccionesConVideo.find(l => l.disponible) || leccionesConVideo[0];
  setLeccionSeleccionada(primera);
  setLoading(false);
 };

  useEffect(() => {
    if (completadas.length && lecciones.length) {
      setProgreso((completadas.length / lecciones.length) * 100);
    }
  }, [completadas, lecciones]);

  const indiceActual = lecciones.indexOf(leccionSeleccionada);
  const totalLecciones = lecciones.length;
  const moduloActual = (estructuraCurso[lang] || estructuraCurso["es"]).find(m => m.id === moduloId);

  const handleAnterior = () => {
    if (indiceActual > 0) setLeccionSeleccionada(lecciones[indiceActual - 1]);
  };

  const handleSiguiente = () => {
    if (indiceActual < totalLecciones - 1) setLeccionSeleccionada(lecciones[indiceActual + 1]);
  };

  const handleCompletar = () => {
    if (leccionSeleccionada?.disponible && !completadas.includes(indiceActual)) {
      setCompletadas([...completadas, indiceActual]);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="curso-layout">
      <aside className="curso-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-back" onClick={() => navigate("/biblioteca")}>
            <svg viewBox="0 0 24 24">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {t(lang, "course", "back")}
          </div>
          <div className="sidebar-course-name">
            {moduloActual?.titulo || moduloId}
          </div>
          <div className="sidebar-progress">
            <div className="progress-bar">
              <div className="progress-indicator" style={{ width: `${progreso}%` }}></div>
            </div>
            <div className="sidebar-prog-text">
              {indiceActual + 1} {t(lang, "course", "of")} {totalLecciones} {lang === "es" ? "lecciones" : "lessons"}
            </div>
          </div>
        </div>

        <div className="sidebar-profile">
          <div className="sidebar-user">{userName}</div>
        </div>

        <ul className="sidebar-list">
          <li className="sidebar-folder">
            <strong className="folder-title">
              {moduloActual?.titulo || moduloId}
            </strong>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {lecciones.map((leccion, index) => {
                const isActive = leccion === leccionSeleccionada;
                const isDone = completadas.includes(index);
                return (
                  <li
                    key={index}
                    className={`sidebar-item ${isActive ? "active" : ""} ${isDone ? "done" : ""}`}
                    onClick={() => setLeccionSeleccionada(leccion)}
                  >
                    <div className="sidebar-item-dot"></div>
                    {leccion.titulo}
                    {!leccion.disponible && (
                      <span style={{ marginLeft: "auto", fontSize: "10px", color: "#444466" }}>
                        {lang === "es" ? "Pronto" : "Soon"}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      </aside>

      <main className="curso-content">
        <div className="curso-nav">
          <div className="curso-nav-title">{leccionSeleccionada?.titulo}</div>
          <div className="nav-btns">
            <button className="btn-nav" onClick={handleAnterior} disabled={indiceActual === 0}>
              <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              {t(lang, "course", "prev")}
            </button>
            <button className="btn-nav next" onClick={handleSiguiente} disabled={indiceActual === totalLecciones - 1}>
              {t(lang, "course", "next")}
              <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>

        {leccionSeleccionada && (
          <div className="curso-section">
            <div className="lang-selector-curso">
              <button className={lang === "es" ? "active" : ""} onClick={() => handleLang("es")}>
                🇦🇷 Español
              </button>
              <button className={lang === "en" ? "active" : ""} onClick={() => handleLang("en")}>
                🇺🇸 English
              </button>
            </div>

            <h3 className="file-title">{leccionSeleccionada.titulo}</h3>

            {leccionSeleccionada.disponible ? (
              <div className="curso-media">
                {leccionSeleccionada.tipo === "video" && (
                  <video src={leccionSeleccionada.url} controls width="100%"></video>
                )}
                {leccionSeleccionada.tipo === "imagen" && (
                  <img src={leccionSeleccionada.url} alt={leccionSeleccionada.titulo} />
                )}
              </div>
            ) : (
              <div style={{
                background: "#0f0f24",
                border: "1px solid #1e1e3a",
                borderRadius: "12px",
                padding: "60px 24px",
                textAlign: "center",
                marginBottom: "20px",
              }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>🎬</div>
                <div style={{ fontSize: "16px", fontWeight: "600", color: "#fff", marginBottom: "8px" }}>
                  {lang === "es" ? "Contenido próximamente" : "Content coming soon"}
                </div>
                <div style={{ fontSize: "13px", color: "#6b6b9a" }}>
                  {lang === "es" ? "Esta lección estará disponible pronto" : "This lesson will be available soon"}
                </div>
              </div>
            )}

            {leccionSeleccionada.disponible && (
              <button
                className={`btn-complete ${completadas.includes(indiceActual) ? "completed" : ""}`}
                onClick={handleCompletar}
              >
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                {t(lang, "course", "complete")}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Curso;